//Library Imports
import { useState, useEffect } from "react";
import styled from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import { Radio, RadioChangeEvent, Spin } from "antd";
import { useHistory } from "react-router-dom";

//Local Imports
import { RootState } from "store";
import AddAction from "./Modals/AddAction";
import Controlls from "./Controlls";
import DropAreaItems from "./DropAreaItems";
import AutomationDragLayer from "./AutomationDragLayer";
import ConfirmDialog from "modules/Common/Components/ConfirmDialog";
import {
  deleteActionModal,
  deleteConditionModal,
  deleteNode,
  getWorkflowDetails,
  resetWorkflow,
  conditionConstraintModal,
  publishWorkflow,
  setInactiveModal,
  updatedWorkflowAPI,
  setPublishModal,
  removeWFEmail,
  updateToasterMessage,
  updateRecentlyPublished,
  shareProfileConfigurationModal,
  setNodeError,
} from "../actions";
import { WFStatusEnum, ControlNameEnum } from "../interfaces";
import { inactivateModalText, publishWorkflowSuccess, saveWorkflowSuccess } from "../constants";
import DialogBox from "modules/Common/Components/DialogBox";
import { getQueryParam } from "utils";
import { findEmailUsage } from "../utils";
import SendingWhite from "../../../assets/img/icons/SendingWhite.svg";
import RoyWhite from "../../../assets/img/icons/royWhite.svg";
import Configuration from "./Modals/Configuration";
import { updateWorkflowCurrentState } from "modules/Campaigns/Edition/actions";

const CreateNewAutomation = () => {
  // Redux States
  const deleteAction = useSelector((state: RootState) => state.automation.modals.deleteAction);
  const deleteCondition = useSelector((state: RootState) => state.automation.modals.deleteCondition);
  const conditionConstraint = useSelector((state: RootState) => state.automation.modals.conditionConstraint);
  const publishModal = useSelector((state: RootState) => state.automation.modals.publish);
  const inactiveModal = useSelector((state: RootState) => state.automation.modals.inactive);
  const deleteNodeObj = useSelector((state: RootState) => state.automation.workflow.toBeDeleteNode);
  const workflowId = useSelector((state: RootState) => state.automation.workflow.detail.id);
  const workflowTriggerEmailId = useSelector((state: RootState) => state.automation.workflow.detail.emailId);
  const workflowTriggerCampaignId = useSelector((state: RootState) => state.automation.workflow.detail.campaignId);
  const workflow = useSelector((state: RootState) => state.automation.workflow.flow);
  const loading: boolean = useSelector((state: RootState) => state.automation.workflow.loading);
  const isRecentlyPublished = useSelector((state: RootState) => state.automation.workflow.isRecentlyPublished);
  const designNotReady = useSelector((state: RootState) => state.automation.workflow.detail.designNotReady);
  const triggerId = useSelector((state: RootState) => state.automation.workflow.detail.triggerId);

  // Local States
  const dispatch = useDispatch();
  const history = useHistory();
  const workflowIdFromUrl = getQueryParam("workflow_id");
  const [radioValue, setRadioValue] = useState(0);
  const [dialogBoxKey, setDialogBoxKey] = useState("");

  const isShareProfileModalVisible = useSelector(
    (state: RootState) => state.automation.modals.shareProfileConfiguration
  );

  const onChangeRadioValue = (e: RadioChangeEvent) => {
    setRadioValue(e.target.value);
  };

  // Component Logic
  useEffect(() => {
    if (!workflowId) {
      getWorkflowDetails(parseInt(workflowIdFromUrl));
    }

    return () => {
      dispatch(resetWorkflow());
    };
  }, []);

  useEffect(() => {
    if (conditionConstraint) {
      setDialogBoxKey("condition");
    } else if (deleteAction) {
      setDialogBoxKey("delete");
    } else if (publishModal) {
      setDialogBoxKey("publish");
    } else if (inactiveModal) {
      setDialogBoxKey("inactive");
    } else {
      setDialogBoxKey("");
    }
  }, [conditionConstraint, deleteAction, publishModal, inactiveModal]);

  const dialogBox: { [key: string]: any } = {
    publish: {
      isWarning: true,
      heading: "",
      isBodyVisible: false,
      bodyText: `Publishing an automation means it will be active and will start executing the actions and conditions for the contacts who meet the trigger and are enrolled in it. Are you ready to publish this workflow automation?`,
      isModalOpen: publishModal,
      isCloseBtnVisible: true,
      closeHandler: (type: string) => {
        if (type === "cancel") {
          dispatch(setPublishModal(false));
        } else {
          // save without publishing
          dispatch(updateToasterMessage(saveWorkflowSuccess));
          publishWorkflow(true);
        }
      },
      proceedBtnText: "Publish",
      proceedHandler: () => {
        dispatch(updateToasterMessage(publishWorkflowSuccess));
        publishWorkflow();
        updateWorkflowCurrentState("active");
      },
      closeBtnText: "Save and Publish later",
    },
    inactive: {
      isWarning: true,
      heading: "",
      isBodyVisible: false,
      bodyText: inactivateModalText,
      isModalOpen: inactiveModal,
      closeHandler: () => {
        dispatch(setInactiveModal(false));
      },
      closeBtnText: "Cancel",
      isCloseBtnVisible: true,
      proceedBtnText: "Proceed",
      proceedHandler: () => {
        updatedWorkflowAPI({
          workflowId,
          status: WFStatusEnum.INACTIVE,
        });
        dispatch(setInactiveModal(false));
      },
    },
    condition: {
      heading: `Can't Perform the Action. `,
      isBodyVisible: true,
      bodyText: "Add Send Email control before condition.",
      isModalOpen: conditionConstraint,
      isCloseBtnVisible: false,
      closeHandler: () => {
        dispatch(conditionConstraintModal(false));
      },
      proceedBtnText: "OK",
      proceedHandler: () => {
        dispatch(conditionConstraintModal(false));
      },
    },
  };

  const confirmDialog = {
    delete: {
      cancel: "Cancel",
      confirm: "Yes",
      titleQuestion: "Are you sure you want to delete this node?",
      title: "",
      icon: RoyWhite,
      isCrossIcon: false,
      className: "confirm-dialog-automation",
      onConfirm: () => {
        let node = workflow[deleteNodeObj.id];
        if (node?.detail?.controlName === ControlNameEnum.SEND_EMAIL) {
          let ids = findEmailUsage(deleteNodeObj.id, node.detail.emailId);
          ids.forEach((item: string) => {
            dispatch(setNodeError(item, true));
          });
        }
        dispatch(deleteNode(deleteNodeObj.id, deleteNodeObj.index || 0));
        if (node?.detail?.controlName === ControlNameEnum.SEND_EMAIL) {
          dispatch(removeWFEmail(node.detail.emailId));
        }
      },
      onCancel: () => {
        if (deleteCondition) {
          dispatch(deleteActionModal(false, "", 0, false));
        } else {
          dispatch(deleteActionModal(false, ""));
        }
      },
    },
    recentlyPublished: {
      cancel: "",
      confirm: "Go to event email",
      titleQuestion: "",
      title: "Your workflow is ready, it will execute after you publish the event email",
      icon: SendingWhite,
      isCrossIcon: true,
      className: "confirm-dialog-automation",
      onConfirm: () => {
        // based on design ready we need to redirect to design or publish page
        history.push(
          `/campaigns/editionedit/${designNotReady ? "design" : "publish"
          }/?campaign_id=${workflowTriggerCampaignId}&email_id=${workflowTriggerEmailId}&workflow_id=${workflowIdFromUrl}&from=workflow`
        );
      },
      onCancel: () => {
        dispatch(updateRecentlyPublished(false));
      },
    },
  };

  let dialogBoxObject = dialogBox[dialogBoxKey];
  let confirmDialogObject = confirmDialog[!isRecentlyPublished ? "delete" : "recentlyPublished"];

  const handleCancelConfiguration = () => {
    dispatch(shareProfileConfigurationModal(false));
  };

  return (
    <>
      <SpinWrapper hidden={!loading}>
        <Spin className="spin-loader" size="large" />
      </SpinWrapper>
      <Wrapper>
        <div className={loading ? "d-flex disabled" : "d-flex"}>
          <DropAreaItems />
          <div className="controll-panel">
            <Controlls />
          </div>
        </div>
        <AddAction />
        <Configuration isVisible={isShareProfileModalVisible} handleCancel={handleCancelConfiguration} />
        <DialogBox
          isWarning={false}
          clickOutsideClose={false}
          heading="Are you sure to delete it?"
          isBodyVisible
          isCenterAligned={false}
          bodyText="If you delete this if/else branch we need to know what you wish to do with all the actions that are
        within the if/else"
          isModalOpen={deleteCondition}
          closeHandler={() => {
            dispatch(deleteConditionModal(false, ""));
          }}
          proceedBtnText="Proceed"
          proceedHandler={() => {
            dispatch(deleteActionModal(true, deleteNodeObj.id, radioValue));
          }}
        >
          {/* Radio Buttons */}
          <div className="radio_group">
            <Radio.Group onChange={onChangeRadioValue} value={radioValue}>
              <Radio value={0} className="options">
                Keep only “YES” path
              </Radio>
              <Radio value={1} className="options">
                Keep only “No” path
              </Radio>
            </Radio.Group>
          </div>
        </DialogBox>
        {/* Added the following component because in the UI we have to open two modals at a time ie.e when deleting a conditional node*/}
        <DialogBox
          isWarning={dialogBoxObject?.isWarning}
          heading={dialogBoxObject?.heading}
          isBodyVisible={dialogBoxObject?.isBodyVisible}
          bodyText={dialogBoxObject?.bodyText}
          isModalOpen={dialogBoxObject?.isModalOpen}
          closeHandler={dialogBoxObject?.closeHandler}
          isCloseBtnVisible={dialogBoxObject?.isCloseBtnVisible}
          closeBtnText={dialogBoxObject?.closeBtnText}
          proceedBtnText={dialogBoxObject?.proceedBtnText}
          proceedHandler={dialogBoxObject?.proceedHandler}
        />
        {((isRecentlyPublished && triggerId !== 10) || deleteAction) && (
          <DialogWrapper>
            <BackOverlay />
            <ConfirmDialog
              cancel={confirmDialogObject.cancel}
              confirm={confirmDialogObject.confirm}
              icon={confirmDialogObject.icon}
              isCrossIcon={confirmDialogObject.isCrossIcon}
              onConfirm={confirmDialogObject.onConfirm}
              onCancel={confirmDialogObject.onCancel}
              title={confirmDialogObject.title}
              titleQuestion={confirmDialogObject.titleQuestion}
              className={confirmDialogObject.className}
            />
          </DialogWrapper>
        )}
        <AutomationDragLayer />
      </Wrapper>
    </>
  );
};

export default CreateNewAutomation;

const SpinWrapper = styled.div`
  .spin-loader {
    position: absolute;
    top: 50%;
    right: 50%;
  }
`;

const Wrapper = styled.div`
  width: 100%;
  display: block;
  position: relative;
  .disabled {
    pointer-events: none;
    opacity: 0.5;
  }
  .controll-panel {
    background-color: #ffffff;
    position: fixed;
    top: 120px;
    right: 0;
    width: 325px;
    height: calc(100vh - 120px);
    z-index: 91;
    box-shadow: 0 0 24px 0 rgba(0, 0, 0, 0.1);
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      height: calc(100vh - 90px);
      top: 90px;
      width: 280px;
    }
  }
  .confirm-dialog-automation {
    left: 48%;
  }
`;

const DialogWrapper = styled.div`
  z-index: 100000;
  top: 50%;
  left: 50%;
  position: fixed;
`;

const BackOverlay = styled.div`
  position: fixed;
  background: grey;
  opacity: 0.2;
  height: 100vh;
  width: 100vw;
  top: 0;
  left: 0;
`;
